/**
 * timeline.js
 *
 * Drives the #timeline slider on the About page.
 *
 * - Responsive items-per-view: 4 → 3 (≤1230px) → 2 (≤998px) → 1 (≤767px)
 * - Arrow buttons scroll by one item at a time
 * - Click-drag (mouse) and touch-drag on the track also work
 * - Arrow SVG path fill goes to #C7C7C7 (light-gray) at boundaries
 */

(function () {
  'use strict';

  const BREAKPOINTS = [
    { maxWidth: 767,  items: 1 },
    { maxWidth: 998,  items: 2 },
    { maxWidth: 1230, items: 3 },
  ];
  const DEFAULT_ITEMS = 4;

  const COLOR_ACTIVE   = '#001D51';
  const COLOR_DISABLED = '#C7C7C7';

  function getItemsPerView() {
    const w = window.innerWidth;
    for (const bp of BREAKPOINTS) {
      if (w <= bp.maxWidth) return bp.items;
    }
    return DEFAULT_ITEMS;
  }

  function init() {
    const section = document.getElementById('timeline');
    if (!section) return;

    const entries = section.querySelector('._entries');
    const items   = Array.from(section.querySelectorAll('._entry'));
    const prevBtn = section.querySelector('._arrow.-prev');
    const nextBtn = section.querySelector('._arrow.-next');

    if (!entries || !items.length || !prevBtn || !nextBtn) return;

    const total = items.length;
    let currentIndex = 0;

    // ── Item width ─────────────────────────────────────────────────────────
    function applyItemWidth() {
      const n = getItemsPerView();
      items.forEach(item => {
        item.style.flexBasis = `calc(100% / ${n})`;
        item.style.minWidth  = `calc(100% / ${n})`;
      });
    }

    function getItemWidth() {
      return entries.offsetWidth / getItemsPerView();
    }

    function getMaxIndex() {
      return Math.max(0, total - getItemsPerView());
    }

    function clamp(i) {
      return Math.min(Math.max(i, 0), getMaxIndex());
    }

    // ── Scroll to index ────────────────────────────────────────────────────
    function scrollToIndex(i, smooth) {
      currentIndex = clamp(i);
      const left = currentIndex * getItemWidth();
      if (smooth === false) {
        entries.scrollLeft = left;
      } else {
        entries.scrollTo({ left, behavior: 'smooth' });
      }
      updateArrows();
    }

    // ── Arrow SVG fill ─────────────────────────────────────────────────────
    // SVG has hardcoded fill="#001D51" on <path> — must set attribute directly.
    function setSvgFill(btn, color) {
      btn.querySelectorAll('path').forEach(p => p.setAttribute('fill', color));
    }

    function updateArrows() {
      const atStart = currentIndex <= 0;
      const atEnd   = currentIndex >= getMaxIndex();
      setSvgFill(prevBtn, atStart ? COLOR_DISABLED : COLOR_ACTIVE);
      setSvgFill(nextBtn, atEnd   ? COLOR_DISABLED : COLOR_ACTIVE);
      // Use aria-disabled instead of disabled so buttons stay clickable
      // (disabled would prevent pointer events entirely)
      prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      nextBtn.setAttribute('aria-disabled', atEnd   ? 'true' : 'false');
    }

    // ── Arrow clicks ───────────────────────────────────────────────────────
    prevBtn.addEventListener('click', () => {
      if (currentIndex > 0) scrollToIndex(currentIndex - 1);
    });
    nextBtn.addEventListener('click', () => {
      if (currentIndex < getMaxIndex()) scrollToIndex(currentIndex + 1);
    });

    // ── Snap disable/enable helpers ────────────────────────────────────────
    // Disabling scroll-snap during the drag lets the container scroll freely
    // so the user sees real movement. We re-enable before snapping.
    function disableSnap() {
      entries.style.scrollSnapType = 'none';
    }

    function enableSnap() {
      entries.style.scrollSnapType = '';
    }

    // ── Mouse drag ─────────────────────────────────────────────────────────
    let dragStartX      = 0;
    let dragStartScroll = 0;
    let isDragging      = false;

    entries.addEventListener('mousedown', (e) => {
      // Ignore clicks on buttons inside the track
      if (e.target.closest('button')) return;
      isDragging      = true;
      dragStartX      = e.clientX;
      dragStartScroll = entries.scrollLeft;
      entries.style.cursor = 'grabbing';
      disableSnap();
    });

    window.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      // Prevent text selection while dragging
      e.preventDefault();
      entries.scrollLeft = dragStartScroll - (e.clientX - dragStartX);
    });

    window.addEventListener('mouseup', () => {
      if (!isDragging) return;
      isDragging = false;
      entries.style.cursor = '';
      enableSnap();
      snapToNearest();
    });

    // ── Touch drag ─────────────────────────────────────────────────────────
    let touchStartX      = 0;
    let touchStartScroll = 0;

    entries.addEventListener('touchstart', (e) => {
      touchStartX      = e.touches[0].clientX;
      touchStartScroll = entries.scrollLeft;
      disableSnap();
    }, { passive: true });

    entries.addEventListener('touchmove', (e) => {
      entries.scrollLeft = touchStartScroll - (e.touches[0].clientX - touchStartX);
    }, { passive: true });

    entries.addEventListener('touchend', () => {
      enableSnap();
      snapToNearest();
    });

    // ── Snap to nearest item ───────────────────────────────────────────────
    function snapToNearest() {
      const w = getItemWidth();
      if (w <= 0) return;
      scrollToIndex(Math.round(entries.scrollLeft / w));
    }

    // ── Resize ─────────────────────────────────────────────────────────────
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        applyItemWidth();
        currentIndex = clamp(currentIndex);
        scrollToIndex(currentIndex, false);
      }, 100);
    });

    // ── Boot ───────────────────────────────────────────────────────────────
    applyItemWidth();
    updateArrows();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
